import React, { useState, useEffect } from 'react';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Tag,
  useToaster,
  Pagination,
  Modal,
  Message,
  IconButton,
  Whisper,
  Tooltip,
  Checkbox
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList, MdDelete, MdInfoOutline } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUsersReservations,
  deleteReservation,
  deleteMultipleReservations
} from '@/services/operation.service';
import { getAdminUsers } from '@/services/user.service';
import { getZones } from '@/services/zone.service';
import { getFloors } from '@/services/floor.service';
import * as XLSX from 'xlsx';
import DetailsDrawer from './DetailsDrawer';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const getStatusText = status => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'Booked';
    case 'ended':
      return 'Checked-In';
    case 'closed':
      return 'Checked-out';
    case 'canceled':
      return 'Canceled';
    default:
      return 'Unknown';
  }
};

const getStatusColor = status => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'green';
    case 'ended':
      return 'blue';
    case 'closed':
      return 'orange';
    case 'canceled':
      return 'red';
    default:
      return 'gray';
  }
};

const DataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { data, loading } = useSelector(state => state.operation);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const { data: floors, loading: floorsLoading } = useSelector(state => state.floor);
  const { data: zones, loading: zonesLoading } = useSelector(state => state.zone);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filters, setFilters] = useState({
    userId: '',
    status: '',
    floorId: '',
    zoneId: '',
    managerId: ''
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState({ open: false, data: null });
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteMultipleModal, setShowDeleteMultipleModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getAdminUsers());
    dispatch(getFloors());
    dispatch(getZones());
  }, [dispatch]);

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);
      dispatch(
        fetchUsersReservations(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'))
      );
    }
  }, [dateRange, dispatch]);

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const filteredData = data.filter(item => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const reservationDate = new Date(item.reservationdate);
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      if (reservationDate < startDate || reservationDate > endDate) {
        return false;
      }
    }
    if (filters.userId && item.user?._id !== filters.userId) return false;
    if (filters.managerId && item.manager?._id !== filters.managerId) return false;
    if (filters.floorId && item.desk?.zone?.floor?._id !== filters.floorId) return false;
    if (filters.zoneId && item.desk?.zone?._id !== filters.zoneId) return false;
    if (filters.status && item.status?.toLowerCase() !== filters.status.toLowerCase()) return false;
    if (searchKeyword) {
      const searchLower = searchKeyword.toLowerCase();
      const fullName = `${item.user?.firstname || ''} ${item.user?.lastname || ''}`.toLowerCase();
      const registrationNumber = item.user?.registrationNumber?.toLowerCase() || '';
      const status = item.status?.toLowerCase() || '';
      const zone = item.desk?.zone?.name?.toLowerCase() || '';
      const floor = item.desk?.zone?.floor?.name?.toLowerCase() || '';
      const resource = item.desk?.name?.toLowerCase() || '';
      return (
        fullName.includes(searchLower) ||
        registrationNumber.includes(searchLower) ||
        getStatusText(status).toLowerCase().includes(searchLower) ||
        zone.includes(searchLower) ||
        floor.includes(searchLower) ||
        resource.includes(searchLower)
      );
    }
    return true;
  });

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ userId: '', status: '', floorId: '', zoneId: '', managerId: '' });
    setPage(1);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const reservationsToExport = filteredData.map(item => ({
      'User Name': item.user
        ? `${item.user?.firstname || ''} ${item.user?.lastname || ''}`
        : item.guest + '(guest)',
      'Registration Number': item.user?.registrationNumber || '',
      Manager: `${item.manager?.firstname || ''} ${item.manager?.lastname || ''}`,
      Date: `${item.reservationdate?.substring(0, 10).split('-').reverse().join('-')} ${
        item.timeslot
      }`,
      Resource: item.desk?.name || '',
      Zone: item.desk?.zone?.name || '',
      Floor: item.desk?.zone?.floor?.name || '',
      Status: getStatusText(item.status),
      'Created At': new Date(item.createdAt).toLocaleString()
    }));
    downloadExcel(reservationsToExport, 'reservations');
  };

  const paginatedData = filteredData.filter((v, i) => {
    const start = limit * (page - 1);
    const end = start + limit;
    return i >= start && i < end;
  });

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  const handleDelete = rowData => {
    setSelectedReservation(rowData);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteReservation(selectedReservation._id));
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Reservation deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);
      if (dateRange && dateRange[0]) {
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
        endDate.setHours(23, 59, 59, 999);
        dispatch(
          fetchUsersReservations(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'))
        );
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('Error deleting reservations');
      toaster.push(
        <Message type="error" duration={3000}>
          {t(errorMessage)}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setShowDeleteModal(false);
  };

  const handleDetails = rowData => {
    setShowDetailsDrawer({ open: true, data: rowData });
  };

  const handleSelectAll = (value, checked) => {
    if (checked) {
      const allIds = filteredData.map(item => item._id);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = id => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleDeleteMultiple = () => {
    setShowDeleteMultipleModal(true);
  };

  const handleConfirmDeleteMultiple = async () => {
    try {
      // Placeholder for future service implementation
      await dispatch(deleteMultipleReservations(selectedRows));
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Reservations deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteMultipleModal(false);
      setSelectedRows([]);
      if (dateRange && dateRange[0]) {
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
        endDate.setHours(23, 59, 59, 999);
        dispatch(
          fetchUsersReservations(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'))
        );
      }
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error deleting reservations')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setShowDeleteMultipleModal(false);
  };

  return (
    <>
      <style>
        {`
          .checkbox-cell {
            display: flex;
            align-items: center;
            height: 100%;
          }
          .rs-table-cell-content {
            display: flex;
            align-items: center;
          }
          .custom-delete-button:hover {
            transform: scale(1.1);
            background-color: #ff4d4f;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
          .custom-delete-button {
            transition: all 0.2s ease;
          }
        `}
      </style>
      <Stack className="table-toolbar" justifyContent="space-between">
        <DateRangePicker
          appearance="default"
          placeholder={t('Select Date Range')}
          style={{ width: 230 }}
          value={dateRange}
          onChange={setDateRange}
          editable={false}
          ranges={[
            {
              label: t('Last 30 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
            },
            {
              label: t('Last 7 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
            },
            {
              label: t('Today'),
              value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
            }
          ]}
        />
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          {selectedRows.length >= 2 && (
            <Whisper
              placement="top"
              trigger="hover"
              speaker={<Tooltip>{t('Delete multiple Reservations')}</Tooltip>}
            >
              <IconButton
                icon={<MdDelete color="white" />}
                color="red"
                appearance="primary"
                onClick={handleDeleteMultiple}
                // className="custom-delete-button"
              />
            </Whisper>
          )}
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <MdFilterList style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
        </Stack>
      </Stack>
      <Table
        height={Math.max(getHeight(window) - 200, 400)}
        data={paginatedData}
        loading={loading}
        rowHeight={80}
        wordWrap="break-word"
      >
        <Column width={50} fixed="left">
          <HeaderCell>
            <Checkbox
              checked={selectedRows.length === filteredData.length}
              indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
              onChange={handleSelectAll}
              className="checkbox-cell"
            />
          </HeaderCell>
          <Cell className="checkbox-cell">
            {rowData => (
              <Checkbox
                checked={selectedRows.includes(rowData._id)}
                onChange={() => handleRowSelect(rowData._id)}
              />
            )}
          </Cell>
        </Column>
        <Column flexGrow={4}>
          <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
          <Cell>
            {rowData =>
              rowData.user
                ? `${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''} `
                : rowData.guest + ` (${t('guest')})`
            }
          </Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>{t('Registration Number')}</HeaderCell>
          <Cell>{rowData => rowData.user?.registrationNumber}</Cell>
        </Column>
        <Column flexGrow={4}>
          <HeaderCell>Date</HeaderCell>
          <Cell>
            {rowData =>
              `${rowData.reservationdate?.substring(0, 10).split('-').reverse().join('-')} ${
                rowData.timeslot
              }`
            }
          </Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Resource</HeaderCell>
          <Cell>{rowData => rowData.desk?.name}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Zone</HeaderCell>
          <Cell>{rowData => rowData.desk?.zone?.name}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>{t('Floor')}</HeaderCell>
          <Cell>{rowData => rowData.desk?.zone?.floor?.name}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>{t('Status')}</HeaderCell>
          <Cell>
            {rowData => (
              <Tag color={getStatusColor(rowData.status)}>{t(getStatusText(rowData.status))}</Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={4}>
          <HeaderCell>{t('Created At')}</HeaderCell>
          <Cell>{rowData => new Date(rowData.createdAt).toLocaleString()}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Manager</HeaderCell>
          <Cell>
            {rowData => {
              const manager = rowData.manager;
              return manager && manager.firstname && manager.lastname
                ? `${manager.firstname} ${manager.lastname}`
                : '';
            }}
          </Cell>
        </Column>
        <Column width={100} flexGrow={0.5} fixed="right">
          <HeaderCell>Actions</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (
              <Stack spacing={6}>
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip>{t('View Details')}</Tooltip>}
                >
                  <IconButton
                    size="xs"
                    icon={<Icon as={MdInfoOutline} />}
                    onClick={() => handleDetails(rowData)}
                    id={`details-${rowData._id}`}
                  />
                </Whisper>
                <IconButton
                  size="xs"
                  icon={<MdDelete color="red" />}
                  onClick={() => handleDelete(rowData)}
                  id={`delete-${rowData._id}`}
                  className="custom-delete-button"
                />
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={filteredData.length}
          limitOptions={[10, 30, 50]}
          limit={limit}
          activePage={page}
          onChangePage={setPage}
          onChangeLimit={handleChangeLimit}
        />
      </div>
      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        floors={floors}
        zones={zones}
        usersLoading={usersLoading}
        floorsLoading={floorsLoading}
        zonesLoading={zonesLoading}
        toaster={toaster}
      />
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Reservation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Are you sure you want to delete this reservation? This action cannot be undone.')}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal open={showDeleteMultipleModal} onClose={() => setShowDeleteMultipleModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Multiple Reservations')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            'Are you sure you want to delete the selected reservations? This action cannot be undone.'
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteMultipleModal(false)} appearance="subtle">
            {t('No')}
          </Button>
          <Button onClick={handleConfirmDeleteMultiple} appearance="primary" color="red">
            {t('Yes')}
          </Button>
        </Modal.Footer>
      </Modal>
      <DetailsDrawer
        open={showDetailsDrawer.open}
        onClose={() => setShowDetailsDrawer({ open: false, data: null })}
        data={showDetailsDrawer.data}
      />
    </>
  );
};

export default DataTable;
